import * as React from 'react';

import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';

import type { FieldError } from 'react-hook-form';
import { gray100, gray60, gray80, red50 } from '../../constants/colors';
import { formLabelWrapper } from '../../constants/location';

type OutlinedInputProps = React.ComponentProps<typeof OutlinedInput>;

type Props = OutlinedInputProps & {
  disabled?: boolean;
  labelText?: string;
  requiredText?: string;
  error?: boolean;
  helperText?: string | FieldError | undefined;
  fieldType?: string;
  inputWidth?: string;
  width?: string;
  height?: string;
  spHeight?: string;
  spWidth?: string;
  formMb?: string | undefined;
  backgroundColor?: string;
  attentionText?: string;
  labelFontSize?: string;
  inputPaddingIs10px8px?: boolean;
  fontSizeIs14px?: boolean;
};

const CustomTextfield = React.forwardRef((props: Props, ref): React.ReactElement => {
  const {
    disabled,
    labelText,
    requiredText,
    error,
    helperText,
    formMb,
    inputWidth,
    width,
    height,
    spWidth,
    spHeight,
    attentionText,
    labelFontSize,
    fontSizeIs14px,
    backgroundColor,
    ...rest
  } = props;

  function inputBorderFocusColor(): string {
    return error ? red50 : gray100;
  }

  function inputBorderColor(): string {
    return error ? red50 : gray60;
  }

  const styles = {
    formLabelWrapper,
    helper: {
      width: width ?? '100%',
      color: `${red50} !important`,
      '@media screen and (max-width:960px)': {
        marginLeft: 0,
        width: spWidth ?? '100%',
        color: `${red50} !important`,
      },
    },
    input: {
      '& .MuiInputBase-input': {
        WebkitTextFillColor: gray100,
        backgroundColor: backgroundColor ?? 'transparent',
      },
      '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: inputBorderColor(),
      },
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: inputBorderFocusColor(),
        borderWidth: '1px',
      },
      '&.MuiOutlinedInput-root:hover': {
        borderColor: inputBorderFocusColor(),
        borderWidth: '1px',
      },
      '@media screen and (max-width:960px)': {
        width: spWidth ?? '100%',
      },
      bgcolor: '#FFF',
      '@media screen and (max-width:600px)': {
        height: spHeight ?? '40px',
      },
      '&.MuiInputBase-inputMultiline': {
        padding: 0,
      },
      '& .MuiOutlinedInput-inputMultiline': {
        fontSize: fontSizeIs14px ? '14px' : '16px',
      },
      '& .MuiOutlinedInput-input': {
        fontSize: fontSizeIs14px ? '14px' : '16px',
      },

      height: height ?? '51px',
      width: inputWidth ?? '400px',
    },
    label: {
      fontSize: labelFontSize ? labelFontSize : '14px',
    },
    required: {
      color: red50,
      fontSize: labelFontSize ? labelFontSize : '14px',
      marginLeft: '2px',
    },
    formMb: { marginBottom: formMb ?? '8px' },
  };

  const textfieldHelper = (): string | undefined => {
    if (helperText === undefined) return;
    return String(helperText);
  };

  return (
    <Typography sx={styles.formMb} align="left" component="div">
      {labelText && (
        <Typography
          align="left"
          sx={{ ...styles.formLabelWrapper, display: 'flex', alignItems: 'center', gap: '4px' }}
          component="div"
        >
          <Typography color={gray80} sx={styles.label} component="span">
            {labelText}
          </Typography>

          {requiredText && (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          )}
        </Typography>
      )}
      <OutlinedInput
        {...rest}
        value={rest.value ?? ''}
        sx={styles.input}
        ref={ref}
        autoComplete="off"
        disabled={disabled}
      />
      {error && (
        <FormHelperText sx={styles.helper} error={error}>
          {error && textfieldHelper()}
        </FormHelperText>
      )}
      {attentionText && (
        <FormHelperText sx={{ color: `${red50} !important` }} error={error}>
          {attentionText}
        </FormHelperText>
      )}
    </Typography>
  );
});
CustomTextfield.displayName = 'CustomTextfield';
export default CustomTextfield;
